import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from './cx';
import s from './Button.module.css';

/**
 * `className` and `style` are removed from every primitive's props on purpose.
 *
 * The brand rules — seal is never interactive, critical has exactly one fill, colour
 * literals live in tokens.css — are enforced by grepping CSS. A call site that can pass
 * `className` or `style` routes around that grep entirely: an inline background carrying a
 * raw seal value is a brand violation check-brand.sh cannot see, because it never reads
 * the call site. Omitting the props makes it a type error instead of a code review.
 */
export type PrimitiveProps<E> = Omit<E, 'className' | 'style'>;

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends PrimitiveProps<ButtonHTMLAttributes<HTMLButtonElement>> {
  /**
   * `destructive` is the single critical fill in the product and belongs to exactly one
   * button: the Delete now confirm, which destroys a key at once. Revoking an API token and
   * rotating a free key are also irreversible, but they are `primary` with a crit-text
   * warning line above them, and Delete, which can be undone for seven days, is `primary`
   * with a warn line — a red button on every dangerous thing teaches people to click red
   * buttons.
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Fills the inline axis. For dialog footers and the nav, not for prose. */
  block?: boolean;
  children?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

const VARIANT: Record<ButtonVariant, string | undefined> = {
  primary: s.primary,
  secondary: s.secondary,
  ghost: s.ghost,
  destructive: s.destructive,
};

const SIZE: Record<ButtonSize, string | undefined> = { sm: s.sm, md: s.md, lg: s.lg };

/**
 * `type` defaults to "button", not the HTML default of "submit". Most dialogs here hold a
 * form, and every one has a header Close that is its cancel; any unmarked button inside
 * that form — a copy, a show-more — that submitted it would be a rotation nobody asked
 * for.
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  block = false,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button {...rest} type={type} className={cx(s.button, VARIANT[variant], SIZE[size], block && s.block)}>
      {children}
    </button>
  );
}

export default Button;
